import { getBrandRuntimeConfig } from './brands';
import { getFeatureFlagsForTenant, isFeatureEnabled } from './features';
import { getTenantRuntimeConfig, getHostnameTenantMap } from './tenants';
import {
  DEFAULT_TENANT_ID,
  FEATURE_KEYS,
  createRuntimeFeatureFlags,
  normalizeBrandId,
  normalizeTenantId,
  type BrandId,
  type FeatureKey,
  type ResolvedRuntimeConfig,
  type RuntimeFeatureFlags,
  type TenantId,
} from './types';

const BRAND_TO_TENANT: Record<BrandId, TenantId> = {
  'trade-proof': 'trade-proof',
  'warrior-trading': 'warrior-trading',
};

const RUNTIME_HINT_PREVIEW_SUFFIXES = ['.vercel.app', '.onrender.com'] as const;
const LOCAL_DEV_WILDCARD_SUFFIXES = ['.localhost', '.local'] as const;
const PRODUCTION_WILDCARD_SUFFIXES = ['.trade-proof.com', '.tradeproof.app'] as const;


function readWindowTenantHint() {
  if (typeof window === 'undefined') return '';

  return String((window as Window & { __BRAND_TENANT?: string }).__BRAND_TENANT || '').trim();
}

function readEnvBrandHint() {
  return String(import.meta.env.VITE_BRAND || '').trim();
}

const parseHostnameFromValue = (value: string) => {
  const next = String(value || '').trim().toLowerCase();
  if (!next) return '';

  try {
    if (next.includes('://')) {
      return new URL(next).hostname.trim().toLowerCase();
    }
  } catch {
    // Fall back to string normalization below.
  }

  return next;
};

export function normalizeHostname(value: string) {
  const parsed = parseHostnameFromValue(value);
  if (!parsed) return '';

  return parsed
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .replace(/:\d+$/, '')
    .trim()
    .toLowerCase();
}

export function resolveTenantIdForHostname(value: string): TenantId {
  const hostname = normalizeHostname(value);
  const tenantId = getHostnameTenantMap()[hostname] ?? resolveTenantIdFromLocalWildcardHostname(hostname);

  if (!tenantId) {
    throw new Error(`Unsupported hostname for tenant bootstrap: ${hostname || '<empty>'}.`);
  }

  return tenantId;
}

/**
 * Local-dev wildcard for dynamic white-label tenants:
 *   {tenantSlug}.localhost  /  {tenantSlug}.local            ->  tenant slug
 *   {tenantSlug}.trade-proof.localhost / .trade-proof.local   ->  tenant slug
 * The second form mirrors the production {slug}.trade-proof.com architecture in
 * local development so hostname semantics stay identical between dev and prod.
 * Known static tenants are resolved by the hostname map first. Dynamic slugs have
 * no static runtime config, so the shared shell uses the default tenant runtime
 * and the backend resolves the real tenant workspace from the hostname.
 */
function resolveTenantIdFromLocalWildcardHostname(hostname: string): TenantId | null {
  if (!hostname) return null;
  if (hostname === 'tradeproof.app' || hostname === 'trade-proof.com') return DEFAULT_TENANT_ID;

  // Match the most-specific suffix first so the two-level local mirror
  // ({slug}.trade-proof.localhost / .trade-proof.local) wins over the bare
  // dev wildcard ({slug}.localhost / {slug}.local), then production wildcards.
  const localBaseSuffix = (['.trade-proof.localhost', '.trade-proof.local'] as const).find((candidate) => hostname.endsWith(candidate));
  const bareLocalSuffix = localBaseSuffix ? null : LOCAL_DEV_WILDCARD_SUFFIXES.find((candidate) => hostname.endsWith(candidate));
  const productionSuffix = localBaseSuffix || bareLocalSuffix ? null : PRODUCTION_WILDCARD_SUFFIXES.find((candidate) => hostname.endsWith(candidate));
  const matchedSuffix = localBaseSuffix || bareLocalSuffix || productionSuffix || null;
  if (!matchedSuffix) return null;

  const subdomain = hostname.slice(0, -matchedSuffix.length);
  if (!subdomain || subdomain.includes('.')) return null;

  return normalizeTenantId(subdomain) || DEFAULT_TENANT_ID;
}

function resolveTenantIdFromBrandHint(brandHint?: string | null) {
  const brandId = normalizeBrandId(brandHint);
  if (!brandId) return null;
  return BRAND_TO_TENANT[brandId];
}

export function resolveRuntimeConfig(options?: {
  hostname?: string | null;
  brandHint?: string | null;
  tenantHint?: string | null;
}): ResolvedRuntimeConfig {
  const hostname = normalizeHostname(
    options?.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  );
  const hostnameTenantMap = getHostnameTenantMap();
  const tenantIdFromHostname = hostname ? hostnameTenantMap[hostname] ?? null : null;

  if (tenantIdFromHostname) {
    const tenant = getTenantRuntimeConfig(tenantIdFromHostname);
    const brand = getBrandRuntimeConfig(tenant.brandId);
    return {
      source: 'hostname',
      hostname,
      tenant,
      brand,
      features: { ...tenant.features },
    };
  }

  // Dynamic white-label tenants opened at {slug}.localhost / {slug}.local resolve
  // to the shared default shell; the backend bootstrap + white-label branding then
  // resolve the real tenant workspace from the hostname.
  const localWildcardTenantId = resolveTenantIdFromLocalWildcardHostname(hostname);
  if (localWildcardTenantId) {
    const tenant = getTenantRuntimeConfig(localWildcardTenantId);
    const brand = getBrandRuntimeConfig(tenant.brandId);
    return {
      source: 'hostname',
      hostname,
      tenant,
      brand,
      features: { ...tenant.features },
    };
  }

  if (canUseRuntimeHintForHostname(hostname)) {
    const tenantIdFromHint = normalizeTenantId(options?.tenantHint);
    if (tenantIdFromHint) {
      const tenant = getTenantRuntimeConfig(tenantIdFromHint);
      const brand = getBrandRuntimeConfig(tenant.brandId);
      return {
        source: 'env',
        hostname,
        tenant,
        brand,
        features: { ...tenant.features },
      };
    }

    const tenantIdFromBrandHint = resolveTenantIdFromBrandHint(options?.brandHint);
    if (tenantIdFromBrandHint) {
      const tenant = getTenantRuntimeConfig(tenantIdFromBrandHint);
      const brand = getBrandRuntimeConfig(tenant.brandId);
      return {
        source: 'env',
        hostname,
        tenant,
        brand,
        features: { ...tenant.features },
      };
    }
  }

  const tenant = getTenantRuntimeConfig(DEFAULT_TENANT_ID);
  const brand = getBrandRuntimeConfig(tenant.brandId);
  return {
    source: 'default',
    hostname,
    tenant,
    brand,
    features: { ...tenant.features },
  };
}

export function getCurrentRuntimeConfig() {
  return resolveRuntimeConfig({
    tenantHint: readWindowTenantHint(),
    brandHint: readEnvBrandHint(),
  });
}

function canUseRuntimeHintForHostname(hostname: string) {
  return RUNTIME_HINT_PREVIEW_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
}

export const currentRuntime = getCurrentRuntimeConfig();

export function isRuntimeTenantResolved(runtime: ResolvedRuntimeConfig = currentRuntime) {
  return runtime.source === 'hostname' || runtime.source === 'env';
}

export function useTenantRuntime() {
  return currentRuntime;
}

export { FEATURE_KEYS, createRuntimeFeatureFlags, getFeatureFlagsForTenant, getHostnameTenantMap, getTenantRuntimeConfig, isFeatureEnabled };
export type { FeatureKey, RuntimeFeatureFlags };
